/**
 * VacationDayPanel.js
 *
 * Side panel that slides from the right showing all vacation entries for a selected day,
 * with staff details and edit/delete actions for each entry.
 *
 * 
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useVacation } from '../../contexts/VacationContext';
import { formatDurationFromMinutes } from '../../utils/TimeSlotUtils';
import VacationModal from '../schedule/VacationModal';

const VacationDayPanel = ({
  isOpen,
  onClose,
  date,
  vacations = [],
  staff = [],
  departments = []
}) => {
  const { t, i18n } = useTranslation('vacation');
  const { deleteVacation, loading: contextLoading } = useVacation();
  const [editingVacation, setEditingVacation] = useState(null);
  const [deletingVacationId, setDeletingVacationId] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  /**
   * Create staff lookup map for efficient fallback lookups
   */
  const staffMap = useMemo(() => {
    const map = new Map();
    staff.forEach(s => map.set(s.id, s));
    return map;
  }, [staff]);

  /**
   * Create department lookup map for efficient fallback lookups
   */
  const departmentMap = useMemo(() => {
    const map = new Map();
    departments.forEach(d => map.set(d.id, d));
    return map;
  }, [departments]);

  /**
   * Get staff name from vacation
   * Prioritizes enriched data from VacationContext
   */
  const getStaffName = useCallback((vacation) => {
    if (vacation.staffName && vacation.staffName !== 'Lädt...') {
      return vacation.staffName;
    }
    const staffMember = staffMap.get(vacation.staffId);
    if (staffMember) return staffMember.name;

    console.warn('Staff not found for vacation:', {
      vacationId: vacation.id,
      staffId: vacation.staffId,
      hasEnrichedData: !!vacation.staffName
    });
    return t('status.deletedStaff');
  }, [staffMap, t]);

  /**
   * Get department name from vacation
   * Prioritizes enriched data from VacationContext
   */
  const getDepartmentName = useCallback((vacation) => {
    if (vacation.departmentName && vacation.departmentName !== 'Lädt...') {
      return vacation.departmentName;
    }
    const department = departmentMap.get(vacation.departmentId);
    if (department) return department.name;

    console.warn('Department not found for vacation:', {
      vacationId: vacation.id,
      departmentId: vacation.departmentId,
      hasEnrichedData: !!vacation.departmentName
    });
    return t('display.unknownDepartment');
  }, [departmentMap, t]);

  /**
   * Format date for display using active i18n language
   */
  const formattedDate = useMemo(() => {
    if (!date) return '';
    return date.toLocaleDateString(i18n.language, {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  }, [date, i18n.language]);

  /**
   * Calculate total duration in minutes
   */
  const totalMinutes = useMemo(() => {
    return vacations.reduce((sum, vacation) => sum + (vacation.durationMinutes || 0), 0);
  }, [vacations]);

  /**
   * Handle edit click
   */
  const handleEditClick = useCallback((vacation) => {
    console.log('Edit vacation clicked:', vacation.id);
    setEditingVacation(vacation);
  }, []);

  /**
   * Handle delete click
   */
  const handleDeleteClick = useCallback((vacationId) => {
    console.log('Delete vacation clicked:', vacationId);
    setDeletingVacationId(vacationId);
    setShowConfirmDelete(true);
  }, []);

  /**
   * Confirm delete
   */
  const handleConfirmDelete = async () => {
    if (!deletingVacationId) return;

    setIsDeleting(true);
    console.log('Deleting vacation:', deletingVacationId);

    try {
      await deleteVacation(deletingVacationId);
      console.log('Vacation deleted successfully');
      setShowConfirmDelete(false);
      setDeletingVacationId(null);

      if (vacations.length <= 1) {
        console.log('No more vacations, closing panel');
        onClose();
      }
    } catch (error) {
      console.error('Error deleting vacation:', error);
      alert(
        t('messages.deleteError') +
        (error.message ? ': ' + error.message : '')
      );
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Cancel delete
   */
  const handleCancelDelete = useCallback(() => {
    console.log('Delete cancelled');
    setShowConfirmDelete(false);
    setDeletingVacationId(null);
  }, []);

  /**
   * Close edit modal
   */
  const handleCloseEditModal = useCallback(() => {
    console.log('Closing edit modal');
    setEditingVacation(null);
  }, []);

  /**
   * Handle overlay click to close panel
   */
  const handleOverlayClick = useCallback((e) => {
    if (e.target.classList.contains('panel-overlay')) {
      onClose();
    }
  }, [onClose]);

  /**
   * Handle escape key to close panel
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && !showConfirmDelete && !editingVacation) {
      onClose();
    }
  }, [onClose, showConfirmDelete, editingVacation]);

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  React.useEffect(() => {
    if (isOpen) {
      console.log('VacationDayPanel opened:', {
        date: formattedDate,
        vacationCount: vacations.length,
        staffCount: staff.length,
        departmentCount: departments.length,
        hasEnrichedData: vacations.some(v => v.staffName && v.departmentName)
      });
    }
  }, [isOpen, formattedDate, vacations.length, staff.length, departments.length, vacations]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="panel-overlay"
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Side Panel */}
      <div
        className={`vacation-day-panel ${isOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="panel-title"
      >
        {/* Panel Header */}
        <div className="panel-header">
          <div className="panel-title">
            <h2 id="panel-title">{formattedDate}</h2>
            <p className="panel-subtitle">
              {t('dayPanel.staffCount', { count: vacations.length })}
            </p>
          </div>
          <button
            className="btn-close"
            onClick={onClose}
            aria-label={t('actions.close')}
            title={t('actions.closeEsc')}
          >
            ✕
          </button>
        </div>

        {/* Panel Content */}
        <div className="panel-content">
          {vacations.length === 0 ? (
            <div className="empty-message">
              <p>{t('dayPanel.noVacations')}</p>
            </div>
          ) : (
            <div className="vacation-list">
              {vacations.map((vacation) => {
                const staffName = getStaffName(vacation);
                const departmentName = getDepartmentName(vacation);
                const isLoadingData = vacation.isLoading ||
                                     vacation.staffName === 'Lädt...' ||
                                     vacation.departmentName === 'Lädt...';

                const durationDisplay = formatDurationFromMinutes(vacation.durationMinutes || 0);

                return (
                  <div
                    key={vacation.id}
                    className={`vacation-item ${isLoadingData ? 'loading' : ''}`}
                  >
                    <div className="vacation-info">
                      <div className="staff-name">
                        {isLoadingData ? (
                          <span className="loading-text">
                            {t('messages.loading')}
                          </span>
                        ) : (
                          staffName
                        )}
                      </div>
                      <div className="vacation-details">
                        <span className="department-name">
                          {isLoadingData ? t('messages.loading') : departmentName}
                        </span>
                        <span
                          className="hours-badge"
                          title={t('display.vacationTime', { duration: durationDisplay })}
                        >
                          {durationDisplay}
                        </span>
                      </div>
                      {vacation.durationMinutes === 0 && !isLoadingData && (
                        <div className="no-shift-notice">
                          {t('preview.noShift')}
                        </div>
                      )}
                    </div>

                    <div className="vacation-actions">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleEditClick(vacation)}
                        disabled={isLoadingData || contextLoading}
                        aria-label={`${t('actions.edit')}: ${staffName}`}
                      >
                        {t('actions.edit')}
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteClick(vacation.id)}
                        disabled={isLoadingData || contextLoading}
                        aria-label={`${t('actions.delete')}: ${staffName}`}
                      >
                        {t('actions.delete')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Panel Footer */}
        {vacations.length > 0 && (
          <div className="panel-footer">
            <div className="total-hours">
              <span className="label">{t('labels.totalHours')}:</span>
              <span className="value">
                {formatDurationFromMinutes(totalMinutes)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingVacation && (
        <VacationModal
          isOpen={true}
          onClose={handleCloseEditModal}
          departments={departments}
          staff={staff}
          mode="edit"
          vacationData={editingVacation}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showConfirmDelete && (
        <div
          className="modal-overlay"
          onClick={handleCancelDelete}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-modal-title"
        >
          <div
            className="confirmation-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="delete-modal-title">{t('modal.deleteTitle')}</h3>
            </div>
            <div className="modal-body">
              <p>{t('modal.confirmDeleteMessage')}</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={handleCancelDelete}
                disabled={isDeleting}
                aria-label={t('actions.cancel')}
              >
                {t('actions.cancel')}
              </button>
              <button
                className="btn btn-danger"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                aria-label={t('actions.delete')}
              >
                {isDeleting ? (
                  <>
                    <span className="spinner-small"></span>
                    {t('messages.deleting')}
                  </>
                ) : (
                  t('actions.delete')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VacationDayPanel;